/** Placeholder iframe height before dynamic sync runs (matches MIN_STABLE_HEIGHT in parent script). */
export const DEFAULT_EMBED_INITIAL_HEIGHT = 600;

interface EmbedParentScriptOptions {
  iframeId: string;
  baseUrl: string;
}

/**
 * Inline parent-page script for organization embed iframes.
 * Must stay in sync with public/embed-parent.js (same behavior, different packaging).
 */
export function buildInlineEmbedParentScript(options: EmbedParentScriptOptions): string {
  const { iframeId, baseUrl } = options;

  return `(function() {
    var iframe = document.getElementById('${iframeId}');
    var baseUrl = '${baseUrl}';
    var baseOrigin = (function(url) {
        try { return new URL(url).origin; } catch (e) { return url.replace(/\\/+$/, ''); }
    })(baseUrl);
    var jumpPending = false;
    var jumpClearTimer = null;
    var minHeightClearTimer = null;
    var lastIframeUrl = null;
    var lastKnownEmbedHeight = 0;
    var ROUTE_TRANSITION_MS = 2500;
    var MIN_STABLE_HEIGHT = 400;

    function lockEmbedMinHeight(reason, suggestedMinHeight) {
        var currentHeight = iframe.offsetHeight || parseInt(iframe.style.height, 10) || 0;
        var minHeight = Math.max(currentHeight, lastKnownEmbedHeight, suggestedMinHeight || 0);

        if (minHeight <= 0) {
            return;
        }

        iframe.style.minHeight = minHeight + 'px';

        if (minHeightClearTimer) {
            clearTimeout(minHeightClearTimer);
        }

        minHeightClearTimer = setTimeout(function() {
            iframe.style.minHeight = '';
            minHeightClearTimer = null;
        }, ROUTE_TRANSITION_MS);
    }

    function updateEmbedHeight(newHeight, reason, meta) {
        meta = meta || {};
        if (jumpPending && newHeight < MIN_STABLE_HEIGHT) {
            return;
        }

        var previousHeight = lastKnownEmbedHeight;
        var lockedMinHeight = parseInt(iframe.style.minHeight, 10) || 0;

        if (lockedMinHeight > 0 && newHeight < lockedMinHeight && newHeight >= MIN_STABLE_HEIGHT) {
            iframe.style.minHeight = '';
            if (minHeightClearTimer) {
                clearTimeout(minHeightClearTimer);
                minHeightClearTimer = null;
            }
            lockedMinHeight = 0;
        }

        var appliedHeight = Math.max(newHeight, lockedMinHeight);

        iframe.style.height = appliedHeight + 'px';
        lastKnownEmbedHeight = appliedHeight;

        if (lockedMinHeight > 0 && newHeight >= lockedMinHeight) {
            iframe.style.minHeight = '';
            if (minHeightClearTimer) {
                clearTimeout(minHeightClearTimer);
                minHeightClearTimer = null;
            }
        }

        var heightDecreased = previousHeight > 0 && newHeight < previousHeight - 50;
        if (jumpPending || meta.scrollToTop || heightDecreased) {
            scrollToEmbedTop(meta.reason || reason || 'height-update-scroll');
        }
    }

    function beginEmbedNavigation(reason, suggestedMinHeight) {
        lockEmbedMinHeight(reason, suggestedMinHeight);
        jumpPending = true;
        if (jumpClearTimer) {
            clearTimeout(jumpClearTimer);
        }
        jumpClearTimer = setTimeout(function() {
            jumpPending = false;
        }, ROUTE_TRANSITION_MS);
        scrollToEmbedTop(reason);
    }

    function getFixedHeaderOffset() {
        var fixedNav = document.querySelector('nav.fixed-top, .fixed-top, header .fixed-top, .scrolling-navbar');
        return fixedNav ? Math.ceil(fixedNav.getBoundingClientRect().height) : 0;
    }

    function scrollToEmbedTop(reason) {
        var scrollRoot = document.scrollingElement || document.documentElement;
        var currentScrollY = window.pageYOffset || scrollRoot.scrollTop || 0;
        var rect = iframe.getBoundingClientRect();
        var fixedOffset = getFixedHeaderOffset();
        var iframeAbsoluteTop = currentScrollY + rect.top;
        var targetScrollY = Math.max(0, iframeAbsoluteTop - fixedOffset);

        window.scrollTo({ top: targetScrollY, left: 0, behavior: 'instant' });

        requestAnimationFrame(function() {
            requestAnimationFrame(function() {
                var afterScrollY = window.pageYOffset || scrollRoot.scrollTop || 0;
                var afterRect = iframe.getBoundingClientRect();
                var remainingOffset = afterRect.top - fixedOffset;

                if (Math.abs(remainingOffset) > 2) {
                    window.scrollTo({
                        top: Math.max(0, afterScrollY + remainingOffset),
                        left: 0,
                        behavior: 'instant'
                    });
                }
            });
        });
    }

    function scrollToEmbedCenter(reason) {
        var scrollRoot = document.scrollingElement || document.documentElement;
        var currentScrollY = window.pageYOffset || scrollRoot.scrollTop || 0;
        var rect = iframe.getBoundingClientRect();
        var fixedOffset = getFixedHeaderOffset();
        var iframeAbsoluteTop = currentScrollY + rect.top;
        var iframeHeight = iframe.offsetHeight || parseInt(iframe.style.height, 10) || rect.height;
        var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        var targetScrollY = iframeAbsoluteTop + (iframeHeight / 2) - (viewportHeight / 2) - fixedOffset;

        window.scrollTo({ top: Math.max(0, targetScrollY), left: 0, behavior: 'instant' });
    }

    window.addEventListener('message', function(event) {
        if (event.origin !== baseOrigin) return;

        if (event.data && event.data.type === 'spotlight-iframe-height') {
            var newHeight = event.data.height + 20;
            updateEmbedHeight(newHeight, 'height-update', {
                scrollToTop: !!event.data.scrollToTop,
                reason: event.data.reason,
                path: event.data.path,
            });
        }

        if (event.data && event.data.type === 'spotlight-iframe-min-height') {
            lockEmbedMinHeight('iframe-min-height-message', event.data.minHeight);
        }

        if (event.data && event.data.type === 'spotlight-iframe-url') {
            if (lastIframeUrl && lastIframeUrl !== event.data.url) {
                beginEmbedNavigation('parent-url-change');
            }

            lastIframeUrl = event.data.url;
        }

        if (event.data && event.data.type === 'spotlight-iframe-scroll-top') {
            scrollToEmbedTop(event.data.reason || 'scroll-top-message');
        }

        if (event.data && event.data.type === 'spotlight-iframe-scroll-to-center') {
            scrollToEmbedCenter(event.data.reason || 'scroll-to-center-message');
        }
    });

    iframe.addEventListener('load', function() {
        setTimeout(function() {
            iframe.contentWindow.postMessage({ type: 'spotlight-request-height' }, baseOrigin);
        }, 500);
    });
})();`;
}

export function buildStickerEmbedCode(options: {
  baseUrl: string;
  stickerSlugOrId: string;
  organizationId: string;
  embedSource?: string;
  standalone?: boolean;
}): string {
  const {
    baseUrl,
    stickerSlugOrId,
    organizationId,
    embedSource = '',
    standalone = true,
  } = options;
  const height = DEFAULT_EMBED_INITIAL_HEIGHT;
  const iframeId = `spotlight-sticker-iframe-${stickerSlugOrId}`;
  const params = new URLSearchParams();

  if (organizationId) {
    params.set('embedOrganizationId', organizationId);
  }
  if (embedSource) {
    params.set('embedSource', embedSource);
  }
  if (standalone) {
    params.set('standalone', '1');
  }

  const embedQuery = params.toString() ? `?${params.toString()}` : '';

  return `<div style="max-width: 1200px; margin: 0 auto;">
    <iframe
        id="${iframeId}"
        src="${baseUrl}/web/embed/sticker/${stickerSlugOrId}${embedQuery}"
        width="100%"
        height="${height}"
        frameborder="0"
        allow="payment; publickey-credentials-get; encrypted-media"
        sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation allow-popups"
        scrolling="no"
        style="border: none; display: block; overflow: hidden; min-height: ${height}px;">
    </iframe>
</div>
<script>
${buildInlineEmbedParentScript({ iframeId, baseUrl })}
<\/script>`;
}

export function buildOrganizationEmbedCode(options: {
  baseUrl: string;
  organizationId: string;
  embedSource?: string;
}): string {
  const {
    baseUrl,
    organizationId,
    embedSource = '',
  } = options;
  const height = DEFAULT_EMBED_INITIAL_HEIGHT;
  const iframeId = `spotlight-org-iframe-${organizationId}`;
  const embedQuery = embedSource ? `?embedSource=${embedSource}` : '';

  return `<div style="max-width: 1200px; margin: 0 auto;">
    <iframe
        id="${iframeId}"
        src="${baseUrl}/web/embed/organization/${organizationId}${embedQuery}"
        width="100%"
        height="${height}"
        frameborder="0"
        allow="payment; publickey-credentials-get; encrypted-media"
        sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation allow-popups"
        scrolling="no"
        style="border: none; display: block; overflow: hidden; min-height: ${height}px;">
    </iframe>
</div>
<script>
${buildInlineEmbedParentScript({ iframeId, baseUrl })}
<\/script>`;
}
